import { useState } from 'react';
import { Button, Input } from '@staff-app/ui';
import {
  INTL_DEVICE_LABELS,
  formatMinor,
  type IntlAllPricedShipment,
  type IntlAllPricing,
  type IntlRoute,
} from '@staff-app/shared-types';

const SOURCE_LABEL: Record<string, string> = {
  AMAZON_PREVIEW: "Amazon's fee",
  FLAT_TABLE: 'flat table',
  TYPED: 'typed',
};

/** Why some lines carry no VAT, naming only the routes this request actually uses. */
function noVatNote(pricing: IntlAllPricing): string {
  const routes = pricing.shipments.filter((s) => s.vatExempt).map((s) => s.route);
  const why = [
    ...(routes.includes('CHINA') ? ['China Fulfill ships DDP with VAT included'] : []),
    ...(routes.includes('HOUSTON') ? ['Houston Fulfill is billed without VAT'] : []),
  ];
  return `${why.join('; ')}, so no VAT is added to those units or their freight.`;
}

/** Root + fulfilment's freight edit: save a figure (minor units) or drop it (null). */
export type SetFreight = (route: IntlRoute, amountMinor: number | null) => Promise<unknown>;

/** "150", "150.5" or "150.50" → 15050; anything else (negative, 3 decimals, text) → null. */
export function parseMoneyToMinor(raw: string): number | null {
  const v = raw.trim().replace(/,/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(v)) return null;
  const [whole, frac = ''] = v.split('.');
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
}

/**
 * WHAT IT WILL COST, as routed — the figures the quote or invoice will carry.
 *
 * VAT IS PER LINE: China ships DDP with VAT included (Paul, 2026-10-27) and Houston is billed
 * without VAT (2026-10-28), so their units and their
 * freight carry no VAT of ours, while MCF and Houston lines do. A model split between China and a
 * taxed route therefore shows as two lines. A shipment with no freight figure (Amazon could not
 * price it, no FX rate) says why — billing refuses until it is resolved, never bills zero.
 */
export function PricingCard({ pricing, onSetFreight }: { pricing: IntlAllPricing; onSetFreight?: SetFreight }) {
  const cur = pricing.currency;
  const anyExempt = pricing.lines.some((l) => l.vatExempt) || pricing.shipments.some((s) => s.vatExempt);
  const charged = pricing.vatPercent != null && pricing.vatPercent > 0;
  const vatTag = (exempt?: boolean | null) =>
    charged ? (exempt ? ' · no VAT' : ` · +${pricing.vatPercent}% VAT`) : '';

  return (
    <div className="space-y-3 rounded-md border bg-muted/40 p-3 text-sm" data-testid="air-pricing">
      <ul className="space-y-1">
        {pricing.lines.map((l) => (
          <li
            key={`${l.deviceModel}-${l.route ?? 'all'}`}
            className="flex justify-between gap-2"
            data-testid={`air-pricing-line-${l.deviceModel}${l.route ? `-${l.route}` : ''}`}
          >
            <span>
              {`${l.quantity} × ${INTL_DEVICE_LABELS[l.deviceModel] ?? l.deviceModel}`}
              {l.routeLabel ? ` — ${l.routeLabel}` : ''}
              <span className="text-xs text-muted-foreground">{vatTag(l.vatExempt)}</span>
            </span>
            <span className="tabular-nums">{formatMinor(l.amount, cur)}</span>
          </li>
        ))}
        {pricing.shipments.map((s) => (
          <FreightRow key={s.route} shipment={s} currency={cur} vatTag={vatTag(s.vatExempt)} onSetFreight={onSetFreight} />
        ))}
      </ul>
      <div className="space-y-1 border-t pt-2">
        <div className="flex justify-between">
          <span className="text-muted-foreground">{'Devices'}</span>
          <span className="tabular-nums">{formatMinor(pricing.devicesTotal, cur)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">{'Freight'}</span>
          <span className="tabular-nums">{pricing.freightTotal != null ? formatMinor(pricing.freightTotal, cur) : '—'}</span>
        </div>
        {charged ? (
          <div className="flex justify-between" data-testid="air-pricing-vat">
            <span className="text-muted-foreground">{`VAT ${pricing.vatPercent}%`}</span>
            <span className="tabular-nums">{pricing.vatMinor != null ? formatMinor(pricing.vatMinor, cur) : '—'}</span>
          </div>
        ) : null}
        <div className="flex justify-between font-medium" data-testid="air-pricing-total">
          <span>{charged ? 'Total incl. VAT' : 'Total'}</span>
          <span className="tabular-nums">
            {(charged ? pricing.totalIncludingVat : pricing.total) != null
              ? formatMinor((charged ? pricing.totalIncludingVat : pricing.total) as number, cur)
              : 'Not priced — see freight'}
          </span>
        </div>
      </div>
      {pricing.freightBreakdown ? (
        <p className="text-xs text-muted-foreground" data-testid="air-pricing-breakdown">
          {`On the quote: “${pricing.freightBreakdown}”`}
        </p>
      ) : null}
      {anyExempt && charged ? (
        <p className="text-xs text-muted-foreground" data-testid="air-pricing-ddp">
          {noVatNote(pricing)}
        </p>
      ) : null}
    </div>
  );
}

/**
 * One route's freight. With `onSetFreight` (root + fulfilment, nothing quoted or invoiced yet) it
 * can be replaced by a typed figure, and a replaced one shows the system's figure beside it with a
 * way back to it. Paul, 2026-10-28.
 */
function FreightRow({
  shipment: s,
  currency,
  vatTag,
  onSetFreight,
}: {
  shipment: IntlAllPricedShipment;
  currency: string;
  vatTag: string;
  onSetFreight?: SetFreight;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const overridden = Boolean(s.overriddenBy);
  const parsed = parseMoneyToMinor(draft);
  const id = `air-pricing-freight-${s.route}`;

  const run = async (amount: number | null) => {
    if (!onSetFreight) return;
    setBusy(true);
    setError(null);
    try {
      await onSetFreight(s.route, amount);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const open = () => {
    setDraft(s.freightMinor != null ? (s.freightMinor / 100).toFixed(2) : '');
    setError(null);
    setEditing(true);
  };

  return (
    <li className="space-y-1" data-testid={id}>
      <div className="flex justify-between gap-2">
        <span>
          {`Freight — ${s.routeLabel}`}
          <span className="text-xs text-muted-foreground">
            {s.freightSource ? ` · ${overridden ? 'edited' : SOURCE_LABEL[s.freightSource] ?? s.freightSource}` : ''}
            {s.speed ? ` · ${s.speed}` : ''}
            {vatTag}
          </span>
          {overridden ? (
            <span className="block text-xs text-muted-foreground" data-testid={`${id}-override`}>
              {`Set by ${s.overriddenBy} · system figure ${s.calculatedMinor != null ? formatMinor(s.calculatedMinor, currency) : 'not available'}`}
            </span>
          ) : null}
          {s.fxNote && !overridden ? <span className="block text-xs text-muted-foreground">{s.fxNote}</span> : null}
          {s.unavailableReason && !overridden ? (
            <span className="block text-xs text-destructive">
              {s.unavailableReason}
              {onSetFreight ? ' Use Edit to enter it.' : ''}
            </span>
          ) : null}
        </span>
        <span className="flex shrink-0 items-start gap-2">
          <span className="tabular-nums">{s.freightMinor != null ? formatMinor(s.freightMinor, currency) : '—'}</span>
          {onSetFreight && !editing ? (
            <button
              type="button"
              className="text-xs text-primary underline-offset-2 hover:underline"
              onClick={open}
              data-testid={`${id}-edit`}
            >
              {'Edit'}
            </button>
          ) : null}
        </span>
      </div>
      {editing ? (
        <div className="flex flex-wrap items-center gap-2" data-testid={`${id}-editor`}>
          <Input
            className="h-8 w-32"
            inputMode="decimal"
            aria-label={`Freight for ${s.routeLabel} (${currency.toUpperCase()})`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            data-testid={`${id}-input`}
          />
          <span className="text-xs text-muted-foreground">{currency.toUpperCase()}</span>
          <Button size="sm" disabled={busy || parsed == null} onClick={() => run(parsed)} data-testid={`${id}-save`}>
            {'Save'}
          </Button>
          {overridden ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => run(null)} data-testid={`${id}-reset`}>
              {'Use system figure'}
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setEditing(false)}>
            {'Cancel'}
          </Button>
          {draft && parsed == null ? (
            <span className="w-full text-xs text-destructive" data-tone="error">
              {'An amount like 150 or 150.00.'}
            </span>
          ) : null}
        </div>
      ) : null}
      {error ? (
        <p className="text-xs text-destructive" data-testid={`${id}-error`}>
          {error}
        </p>
      ) : null}
    </li>
  );
}
